/* eslint-disable indent */
import './style.scss';

import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { formatCurrency, formatDatetime } from 'utils/func';
import { useGetDataForRegisterResult } from 'utils/hooks/projects';
import { COUNCIL_TYPE_CODE } from 'constant';
import ResearchFields from './ResearchFields';

export default function GeneralInfo({ data, isLiquidation }) {
  const { t } = useTranslation();

  const { projectId } = useParams();

  const { data: councilData } = useGetDataForRegisterResult(
    projectId,
    isLiquidation ? COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL : COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL,
    {}
  );

  const projectProposalOrganizations = data?.projectProposal?.projectProposalOrganizations[0];
  const projectOwner = data?.projectProposal?.projectProposalContacts?.find(
    (item) => item.projectRole.code === 'OWNER'
  );

  return (
    <div className="general-container">
      {/* A */}
      <h4 className="font-bold mb-1">{t('registerResult.generalInfo.title')} </h4>
      {/* A1 */}
      <section>
        <h4>{t('registerResult.generalInfo.taskName')}</h4>
        <p>
          &emsp;- &ensp;
          <span className="font-bold inline-block">
            {t('registerResult.generalInfo.vietnameseName')}:
          </span>{' '}
          {data?.projectProposal?.vietnameseName}
        </p>
        <p>
          &emsp;- &ensp;
          <span className="font-bold inline-block">
            {t('registerResult.generalInfo.englishName')}:
          </span>{' '}
          {data?.projectProposal?.englishName}
        </p>
      </section>
      {/* A2 */}
      <section>
        <h4>{t('registerResult.generalInfo.code')}</h4>
        <p>&emsp;&ensp;{data?.projectCode}</p>
      </section>
      {/* A3 */}
      <section>
        <h4>{t('registerResult.generalInfo.researchField')}</h4>
        <ResearchFields
          researchFields={data?.projectProposal?.projectProposalResearchFields ?? []}
        />
      </section>
      {/* A4  */}
      <section>
        <h4>{t('registerResult.generalInfo.researchType')}</h4>
        <p> &emsp;&ensp;{data?.projectProposal?.researchType?.name}</p>
      </section>
      {/* A5  */}
      <section>
        <h4>{t('registerResult.generalInfo.implementationTime.title')}</h4>
        <p>
          &emsp;- &ensp;{t('registerResult.generalInfo.implementationTime.from')}{' '}
          {formatDatetime(data?.startDate, 'MM/YYYY')}{' '}
          {t('registerResult.generalInfo.implementationTime.to')}{' '}
          {formatDatetime(data?.endDate, 'MM/YYYY')}
        </p>
        <p>
          &emsp;- &ensp;{t('registerResult.generalInfo.implementationTime.extension')}
          {councilData?.projectDeadlineExtensions?.length > 0
            ? ` ${formatDatetime(
                councilData?.projectDeadlineExtensions[0]?.extendDate,
                'DD/MM/YYYY'
              )}`
            : '......'}{' '}
        </p>
      </section>
      {/* A6  */}
      <section>
        <h4>{t('registerResult.generalInfo.expenditure.title')}</h4>
        <p>
          &emsp;- &ensp;{t('registerResult.generalInfo.expenditure.totalExpenditure')}{' '}
          <span className="font-italic">{formatCurrency(data?.totalExpenditure)}</span>{' '}
          {t('registerResult.generalInfo.expenditure.include')}
        </p>
        <p>
          &emsp;&emsp;- &ensp;{t('registerResult.generalInfo.expenditure.fromVNU')}{' '}
          <span className="font-italic">{formatCurrency(data?.totalExpenditure)}</span>
        </p>
        <p>
          &emsp;&emsp;- &ensp;{t('registerResult.generalInfo.expenditure.fromFunding')}{' '}
          <span className="font-italic">{formatCurrency(data?.expenditureFromFunding)}</span>
        </p>
      </section>
      {/* A7  */}
      <section>
        <h4>{t('registerResult.generalInfo.owner.title')}</h4>
        <p>
          &emsp;
          <span className="inline-block">
            {t('registerResult.generalInfo.owner.academicAndName')}
          </span>{' '}
          {projectOwner?.contact?.fullname}
        </p>
        <p>
          &emsp;
          <span className="inline-block">
            {t('registerResult.generalInfo.owner.birthDate')}
          </span>{' '}
          {formatDatetime(projectOwner?.dob, 'DD/MM/YYYY')}
          <span className="inline-block ml-6">
            {t('registerResult.generalInfo.owner.gender')}
          </span>{' '}
          {projectOwner?.gender ? t(`gender.${projectOwner?.gender}`) : '.......'}
        </p>
        <p>
          &emsp;
          <span className="inline-block">{t('registerResult.generalInfo.owner.phone')}</span>{' '}
          {projectOwner?.mobile}
          <span className="ml-6">{t('registerResult.generalInfo.owner.fax')}</span>{' '}
          {projectOwner?.contact?.fax}
        </p>
        <p>
          &emsp;
          <span>{t('registerResult.generalInfo.owner.email')}</span> {projectOwner?.contact?.email}
        </p>
      </section>
      {/* A8  */}
      <section>
        <h4>{t('registerResult.generalInfo.organization.title')}</h4>
        <p>
          &emsp;
          <span>{t('registerResult.generalInfo.organization.name')}</span>{' '}
          {projectProposalOrganizations?.name}
        </p>
        <p>
          &emsp;
          <span>{t('registerResult.generalInfo.organization.leaderName')}</span>{' '}
          {projectProposalOrganizations?.chief}
        </p>
        <p>
          &emsp;
          <span>{t('registerResult.generalInfo.organization.phone')}</span>
          {projectProposalOrganizations?.phone} &emsp;&emsp;{' '}
          {t('registerResult.generalInfo.organization.fax')}
          {projectProposalOrganizations?.fax}
        </p>
        <p>
          &emsp;
          <span>{t('registerResult.generalInfo.organization.email')}</span>{' '}
          {projectProposalOrganizations?.email}
        </p>
      </section>
      {/* A9  */}
      <section>
        <h4>
          {t(
            `registerResult.generalInfo.acceptance.${isLiquidation ? 'titleLiquidation' : 'title'}`
          )}
        </h4>
        <p>
          &emsp;- &ensp;
          <span>
            {t('registerResult.generalInfo.acceptance.decisionNumber', {
              council_type: isLiquidation
                ? t('registerResult.generalInfo.acceptance.liquidation')
                : t('registerResult.generalInfo.acceptance.acceptance'),
            })}
          </span>{' '}
          ... <span>{t('registerResult.generalInfo.acceptance.decisionDate')}</span> ...{' '}
        </p>
        <p>
          &emsp;- &ensp;
          <span>
            {t('registerResult.generalInfo.acceptance.meeting', {
              council_type: isLiquidation
                ? t('registerResult.generalInfo.acceptance.liquidation')
                : t('registerResult.generalInfo.acceptance.acceptance'),
            })}
            :{' '}
          </span>
          {formatDatetime(
            councilData?.council?.projectProposalCouncils?.[0]?.timeMet,
            'DD/MM/YYYY'
          )}{' '}
          &emsp;&emsp; {t('registerResult.generalInfo.acceptance.at')} ...
        </p>
        {!isLiquidation && (
          <p className="my-1 text-red-500">
            &emsp;- &ensp;
            <span>
              {t('registerResult.generalInfo.acceptance.rank', {
                council_type: isLiquidation
                  ? t('registerResult.generalInfo.acceptance.liquidation')
                  : t('registerResult.generalInfo.acceptance.acceptance'),
              })}
            </span>{' '}
            {councilData?.council?.projectProposalCouncils?.[0]?.councilStatus &&
              t(
                `council.viewList.${councilData?.council?.projectProposalCouncils?.[0]?.councilStatus}`
              )}
          </p>
        )}
      </section>
    </div>
  );
}

GeneralInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  isLiquidation: PropTypes.bool,
};

GeneralInfo.defaultProps = {
  isLiquidation: false,
};
